import type { AwsRum } from 'aws-rum-web';

const APP_VERSION = '1.0.0';
const AWS_REGION = 'us-east-1';
const RUM_ENDPOINT = `https://dataplane.rum.${AWS_REGION}.amazonaws.com`;

export type PartialRUMConfig = {
    applicationId: string;
    rumGuestRoleArn: string;
    identityPoolId: string;
};

export const initializeAWSRum = async (
    partialConfig: PartialRUMConfig
): Promise<AwsRum | null> => {
    const { applicationId, identityPoolId, rumGuestRoleArn } = partialConfig;

    try {
        // Since the library is only used client side and is big (>100kb) we download it asynchronously
        const awsRum = await import('aws-rum-web').then(({ AwsRum }) => {
            return new AwsRum(applicationId, APP_VERSION, AWS_REGION, {
                sessionSampleRate: 1,
                guestRoleArn: rumGuestRoleArn,
                identityPoolId,
                endpoint: RUM_ENDPOINT,
                telemetries: ['performance', 'errors', 'http'],
                enableXRay: true,
            });
        });

        // Use addEventListener instead of direct assignment to avoid
        // "Attempted to assign to readonly property" errors on WebKit browsers
        // (Safari, Mobile Safari, Chrome on iOS) where window.onerror may be readonly
        try {
            window.addEventListener('error', (event) => {
                awsRum.recordError(event.message || event.error);
            });
        } catch {
            // Silently fail if we can't attach error handler
        }

        return awsRum;
    } catch (error) {
        // Gracefully handle RUM initialization failures.
        // This can happen with ad blockers, Safari ITP, corporate firewalls,
        // or when AWS STS credentials cannot be retrieved.
        if (process.env.NODE_ENV === 'development') {
            console.warn(
                'AWS RUM initialization failed (monitoring will be disabled):',
                error
            );
        }
        return null;
    }
};
